import React, {
  useState, useCallback, useRef, useEffect,
} from 'react'
import { Captcha, FloatingInput, FloatingPasswordInput } from '@usap/uicomponents'
import CreateAccount from '../../../../../helpers/CreateAccount'
import useInvisibleRecaptcha from '../../../../../hooks/useInvisibleRecaptcha'
import useMembershipV2Enabled from '../../../../../hooks/useMembershipV2Enabled'
import captchaConfig from '../../../../../../config/captcha-config'
import BenefitsBlock from './BenefitsBlock'
import LoyaltySocialButtons from './LoyaltySocialButtons'
import LoyaltyErrorToast from './LoyaltyErrorToast'
import {
  ModalBody,
  FieldRow,
  FieldsStack,
  PrimaryButton,
  SecondaryLink,
  CustomCheckbox,
  CheckboxBox,
  Divider,
  LegalText,
  PasswordHint,
  ErrorText,
} from './LoyaltySignInModal.css'

// Hydra returns `errors[0].code === 122` for "email already exists" (HTTP 422).
// Keep this constant local — it's the only code we branch on.
const EMAIL_EXISTS_ERROR_CODE = 122

type CreateAccountFormProps = {
  onSuccess: (email: string) => void
  onSwitchToSignIn: () => void
}

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 6.5L4.5 9L10 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const isValidPassword = (pw: string) => pw.length >= 8 && /[a-zA-Z]/.test(pw) && /[0-9]/.test(pw) && !/\s/.test(pw)

const CreateAccountForm: React.FC<CreateAccountFormProps> = ({ onSuccess, onSwitchToSignIn }) => {
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [marketingOptIn, setMarketingOptIn] = useState(true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [emailExists, setEmailExists] = useState(false)
  const [captcha, setCaptcha] = useState<string | false>(false)

  // Track which field has focus so FloatingInput / FloatingPasswordInput can
  // render the appropriate focused state. Mirrors LoginFormWMO.
  const [focusedInput, setFocusedInput] = useState('')
  const onFocusFun = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => setFocusedInput(e.target.name),
    [],
  )
  const onBlurFun = useCallback(() => setFocusedInput(''), [])

  // Ref to the @usap/uicomponents <Captcha> wrapper — exposes resetCaptcha /
  // executeRecaptchaSync via useImperativeHandle. The raw react-google-recaptcha
  // does not, which is what broke this form under f42=b. Typed `any` because
  // the wrapper's TS types aren't published.
  const inVisibleRecaptchaRef = useRef<any>(null)
  const isInvRecaptchaEnabled = useInvisibleRecaptcha()
  // 1% A/B loyalty rollout: f98=b → ask Hydra to enrol this new account
  // into the loyalty program (Hydra forwards `enroll_to_loyalty` in its
  // outbound EventBridge `customer.created` detail to OMS).
  const enrollToLoyalty = useMembershipV2Enabled()

  const pwValid = isValidPassword(password)
  const canSubmit = firstName.trim() && lastName.trim() && email.trim() && pwValid

  const doCreateAccount = useCallback(async (token: string) => {
    setLoading(true)
    setError('')
    setEmailExists(false)
    try {
      await CreateAccount({
        firstName,
        lastName,
        emailAddress: email,
        password,
        captcha: token,
        isInvRecaptchaEnabled,
        ltrPromo: marketingOptIn,
        promotionalEmail: marketingOptIn,
        enrollToLoyalty,
      })
      onSuccess(email)
    } catch (err: any) {
      // Hydra rejects with `{ errors: [{ code, message, status }] }` shape
      // (see CreateAccount.js — `err.response.data` is what's thrown). Code 122
      // is the canonical "email already exists" signal (422). Render a toast
      // with a Recover-Password link for that case; fall back to a plain
      // message for everything else.
      const code = err?.errors?.[0]?.code
      if (code === EMAIL_EXISTS_ERROR_CODE) {
        setEmailExists(true)
      } else {
        const msg = err?.errors?.[0]?.message ||
          err?.message ||
          err?.data?.message ||
          'Unable to create account. Please try again.'
        setError(msg)
      }
    } finally {
      setLoading(false)
    }
  }, [firstName, lastName, email, password, isInvRecaptchaEnabled, marketingOptIn, enrollToLoyalty, onSuccess])

  useEffect(() => {
    if (captcha && isInvRecaptchaEnabled) {
      doCreateAccount(captcha as string).then(() => setCaptcha(false))
    }
  }, [captcha, isInvRecaptchaEnabled, doCreateAccount])

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    if (isInvRecaptchaEnabled) {
      inVisibleRecaptchaRef.current?.resetCaptcha()
      // eslint-disable-next-line no-sync
      inVisibleRecaptchaRef.current?.executeRecaptchaSync()
      return
    }
    await doCreateAccount(captcha as string)
  }, [canSubmit, captcha, isInvRecaptchaEnabled, doCreateAccount])

  const onFirstNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value),
    [],
  )
  const onLastNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setLastName(e.target.value),
    [],
  )
  const onEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value),
    [],
  )
  const onPasswordChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value),
    [],
  )
  const resetFirstName = useCallback(() => setFirstName(''), [])
  const resetLastName = useCallback(() => setLastName(''), [])
  const resetEmail = useCallback(() => setEmail(''), [])
  const resetPassword = useCallback(() => setPassword(''), [])
  const onMarketingChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setMarketingOptIn(e.target.checked),
    [],
  )

  return (
    <ModalBody as="form" onSubmit={handleSubmit}>
      <BenefitsBlock />
      <LoyaltySocialButtons buttonText="Continue With Google" form="Create Account" dividerBelow />
      {emailExists && (
        <LoyaltyErrorToast message="Email is already registered." />
      )}
      <FieldsStack>
        <FieldRow>
          <FloatingInput
            id="firstName"
            name="firstName"
            type="text"
            label="First Name"
            value={firstName}
            onChange={onFirstNameChange}
            cross={firstName.length > 0}
            onFocus={onFocusFun}
            onBlur={onBlurFun}
            hasFocus={focusedInput === 'firstName'}
            resetClick={resetFirstName}
            required={false}
            isWmoEnabled
          />
          <FloatingInput
            id="lastName"
            name="lastName"
            type="text"
            label="Last Name"
            value={lastName}
            onChange={onLastNameChange}
            cross={lastName.length > 0}
            onFocus={onFocusFun}
            onBlur={onBlurFun}
            hasFocus={focusedInput === 'lastName'}
            resetClick={resetLastName}
            required={false}
            isWmoEnabled
          />
        </FieldRow>
        <FloatingInput
          id="emailAddress"
          name="emailAddress"
          type="email"
          label="Email Address"
          value={email}
          onChange={onEmailChange}
          cross={email.length > 0}
          onFocus={onFocusFun}
          onBlur={onBlurFun}
          hasFocus={focusedInput === 'emailAddress'}
          resetClick={resetEmail}
          required={false}
          isWmoEnabled
        />
        <FloatingPasswordInput
          id="password"
          name="password"
          label="Password"
          value={password}
          onChange={onPasswordChange}
          cross={password.length > 0}
          onFocus={onFocusFun}
          onBlur={onBlurFun}
          hasFocus={focusedInput === 'password'}
          resetClick={resetPassword}
          required={false}
          isWmoEnabled
        />
        <PasswordHint>
          At least 8 characters · At least 1 letter · At least 1 number · No spaces
        </PasswordHint>
      </FieldsStack>

      {React.createElement(Captcha as any, {
        // Distinct id from SignInForm's 'loyalty-signin-captcha' — the Captcha
        // wrapper uses this id for a hidden-response DOM lookup, so two forms
        // rendered on the same page must not collide.
        id: 'loyalty-create-captcha',
        name: 'loyalty-create-captcha',
        siteKey: captchaConfig.siteKey,
        setCaptcha,
        reset: false,
        recaptchaType: isInvRecaptchaEnabled ? 'invisible' : '',
        ref: inVisibleRecaptchaRef,
      })}

      {error && <ErrorText>{error}</ErrorText>}

      <PrimaryButton
        type="submit"
        disabled={!canSubmit || loading || (!isInvRecaptchaEnabled && !captcha)}
      >
        {loading ? 'Creating Account…' : 'Create Account'}
      </PrimaryButton>

      <SecondaryLink type="button" onClick={onSwitchToSignIn}>
        Go Back to Sign In
      </SecondaryLink>

      <CustomCheckbox>
        <input
          type="checkbox"
          checked={marketingOptIn}
          onChange={onMarketingChange}
        />
        <CheckboxBox><CheckIcon /></CheckboxBox>
        <span className="label">I would like to receive emails with exclusive coupons, promotions, and news from CarParts.com.</span>
      </CustomCheckbox>

      <Divider />

      <LegalText>
        By creating an account, you agree to CarParts.com&apos;s
        {' '}
        <a href="/content/terms-conditions" target="_blank" rel="noopener noreferrer">Terms and Conditions</a>
        ,
        {' '}
        <a href="/content/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
        , and
        {' '}
        <a href="/help-center/carparts-plus-membership-terms-and-conditions" target="_blank" rel="noopener noreferrer">CarParts+ Membership Program Terms</a>
      </LegalText>
    </ModalBody>
  )
}

export default CreateAccountForm
